// Best-effort audit log: resolves the dashboard's string-keyed robot
// (fleet UUID + human name) to robot-hub's int Robot.id so we can POST
// to /events. Every step is optional — if the user isn't logged in,
// /org/{id}/robots 401s, the network is gone, or the robot isn't in
// robot-hub yet, we silently skip.

import { logAuditEvent, type AuditExtraInfo } from '@shared/lib/auditLogger';
import { isKiosk } from '@shared/lib/constants';
import { useOrgStore } from '@/stores/useOrgStore';
import { getCurrentRobotKey } from '@/hooks/useCurrentRobot';
import { resolveRobotIntId } from './robot-id-resolver';
import { getAuthToken } from './authToken';

export function audit(extraInfo: AuditExtraInfo): void {
  try {
    // Cloud requires a Cognito token; kiosk routes through the local auth
    // proxy which injects credentials, so no token is expected there.
    if (!getAuthToken() && !isKiosk) return;
    const robotKey = getCurrentRobotKey();
    if (!robotKey) return;

    const { me, selectedOrgId } = useOrgStore.getState();
    if (!me) return;
    const effectiveOrgId = selectedOrgId ?? me.org_id;
    if (effectiveOrgId == null) return;

    void resolveRobotIntId(effectiveOrgId, robotKey).then((robotId) => {
      if (robotId == null) return;
      // me.id is the user row when /me returned a user principal, but the
      // robot row id when /me returned a robot principal (kiosk via auth
      // proxy). Robot-emitted events have no acting user — pass null so the
      // hub doesn't store a robot id in event.user_id.
      const userId = me.type === 'user' ? me.id : null;
      logAuditEvent({ robotId, userId, extraInfo, token: getAuthToken() });
    }).catch(() => {});
  } catch {
    // never propagate
  }
}
